import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, Transition as _Transition } from "vue"
import _imports_0 from '@/assets/pencil-gray.svg'


const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "w-full h-screen fixed top-0 left-0 flex items-start justify-center bg-black/75 z-[1000]" }
const _hoisted_3 = { class: "flex gap-2 items-center" }
const _hoisted_4 = { class: "text-xl font-semibold" }

import client from "@/utils/axios";
import { errorToast, successToast } from "@/utils/custom-toast";
import { ref, computed } from "vue";
import QuestionnaireModal from "@/components/Questionnaire/parts/QuestionnaireModal.vue";
import QuestionnaireProgressBar from "@/components/Questionnaire/parts/QuestionnaireProgressBar.vue";
import AbnahmeInitial from "@/components/Questionnaire/abnahme/AbnahmeInitial.vue";
import AbnahmeApointmentCompleted from "@/components/Questionnaire/abnahme/AbnahmeApointmentCompleted.vue";
import AbnahmeAppointmentNotCompleted from "@/components/Questionnaire/abnahme/AbnahmeAppointmentNotCompleted.vue";
import AbnahmeAppointmentFinal from "@/components/Questionnaire/abnahme/AbnahmeAppointmentFinal.vue";
import { Question } from "@/types";
import AbnahmeDataMissingTextArea from "../Questionnaire/abnahme/AbnahmeDataMissingTextArea.vue";
import { parseQcAbnahmeAnswers } from "@/utils/parse-qc-abnahme-answers";
import { useTranslate } from "@/composables/useTranslate";

type Props = {
  isAbnahmeModalOpen: boolean;
  projectId: string;
  phaseId: number;
  phaseDetails: any;
};

type Emits = {
  (event: "onClose"): void;
  (event: "onSubmit"): void;
  (event: "generateReport"): void;
};

export default /*@__PURE__*/_defineComponent({
  __name: 'AbnahmeModal',
  props: {
    isAbnahmeModalOpen: { type: Boolean },
    projectId: {},
    phaseId: {},
    phaseDetails: {}
  },
  emits: ["onClose", "onSubmit", "generateReport"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emits = __emit;
const { t } = useTranslate();
const questions = [
  {
    slug: "abnahme-initial",
    modalTitle: t("questionnaire.completing_abnahme").value,
    question: t("questionnaire.was_appointment_completed").value,
    answers: ["abnahme-outcome", "appointment-not-completed"],
    value: "",
  },
  {
    slug: "abnahme-outcome",
    modalTitle: t("questionnaire.completing_abnahme").value,
    question: t("questionnaire.what_is_outcome_abnahme").value,
    answers: ["abnahme-passed", "data-missing", "abnahme-failed"],
    value: "",
  },
  {
    slug: "abnahme-passed",
    modalTitle: t("questionnaire.review_abnahme").value,
    answers: [],
    data: {
      ibnData: "",
    },
    value: "",
  },
  {
    slug: "data-missing",
    modalTitle: t("questionnaire.completing_abnahme").value,
    answers: [],
    data: {
      description: "",
    },
    value: "",
  },
  {
    slug: "abnahme-failed",
    modalTitle: t("questionnaire.completing_abnahme").value,
    answers: [],
    data: {
      description: "",
      whatToSchedule: "",
      days: 0,
      missingMaterial: "",
      dispoNotes: "",
      fieldInstruction: "",
    },
    value: "",
  },
  {
    slug: "data-missing-review",
    modalTitle: t("questionnaire.review_abnahme").value,
    answers: [],
    data: null,
    value: "",
  },
  {
    slug: "appointment-not-completed",
    modalTitle: t("questionnaire.completing_abnahme").value,
    question: t("questionnaire.completing_abnahme").value,
    answers: [],
    data: {
      description: "",
      whatToSchedule: "",
      days: 0,
      missingMaterial: "",
      dispoNotes: "",
      fieldInstruction: "",
    },
  },
  {
    slug: "abnahme-failed-review",
    modalTitle: t("questionnaire.review_abnahme").value,
    answers: [],
    data: null,
    value: "",
  },
  {
    slug: "appointment-not-completed-review",
    modalTitle: t("questionnaire.review_abnahme").value,
    answers: [],
    data: null,
    value: "",
  },
];

const questionnaireProgress = computed(() => {
  let percentage = 100;
  if (selectedQuestion.value && selectedQuestion.value) {
    switch (selectedQuestion.value.slug) {
      case "abnahme-initial":
        percentage = 15;
        break;

      case "abnahme-outcome":
        percentage = 50;
        break;
      case "appointment-not-completed":
      case "data-missing":
      case "abnahme-failed":
        percentage = 70;
        break;
      case "appointment-not-completed-review":
      case "data-missing-review":
      case "abnahme-failed-review":
        percentage = 100;
        break;
    }
  }

  return percentage;
});

const questionsState = ref<Array<Question>>([...questions]);
const isModalOpen = computed(() => props.isAbnahmeModalOpen);
const isLoading = ref(false);
const selectedQuestion = ref<Question | undefined>(
  questionsState.value
    ? questionsState.value.find(
        (item: Question) => item.slug === "abnahme-initial"
      )
    : undefined
);

const dataMissingReviewQuestions = computed(() => {
  let dataMissingQuestion = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
  if (dataMissingQuestion && dataMissingQuestion.data) {
    return [
      {
        question: "What data is missing? Please write here",
        answer: dataMissingQuestion.data.description,
      },
    ];
  }
  return [];
});

const abnahmeFailedReviewQuestions = computed(() => {
  let abnahmeFailedData = questionsState.value.find(
    (item: Question) => item.slug === "abnahme-failed"
  );
  if (abnahmeFailedData && abnahmeFailedData.data) {
    let tempQuestions = parseQcAbnahmeAnswers(
      abnahmeFailedData.data,
      "abnahme"
    );
    return tempQuestions;
  }
  return [];
});

const appointmentNotCompletedReviewQuestions = computed(() => {
  let appointmentNotCompleted = questionsState.value.find(
    (item: Question) => item.slug === "appointment-not-completed"
  );
  if (appointmentNotCompleted && appointmentNotCompleted.data) {
    let tempQuestions = parseQcAbnahmeAnswers(
      appointmentNotCompleted.data,
      "appointment"
    );
    return tempQuestions;
  }
  return [];
});

const currentPhase = computed(() => {
  let currentPhaseProgress = props.phaseDetails?.phases.find(
    (phase: any) => phase.phaseId === props.phaseId
  );
  return currentPhaseProgress;
});

const onClose = () => {
  if (questionsState.value) {
    questionsState.value = [...questions];
  }
  selectedQuestion.value = questionsState.value
    ? questionsState.value.find(
        (item: Question) => item.slug === "abnahme-initial"
      )
    : undefined;
  emits("onClose");
};

const abnahmeInitialOnSubmit = (message: string) => {
  let tempQuestions = questionsState.value.map((item: Question) => {
    if (item.slug === "abnahme-initial") {
      item.value = message;
    }
    return item;
  });

  questionsState.value = tempQuestions;
  let nextQuestion = "";
  if (message && message.includes("Yes")) {
    nextQuestion = "abnahme-outcome";
  } else {
    nextQuestion = "appointment-not-completed";
  }

  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === nextQuestion
  );
};

const onAbnahmePassed = () => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "abnahme-passed"
  );
};

const onAbnahmePassedSubmit = (ibnDate: string, generateReport?: boolean) => {
  let payload = {
    actionId: 4,
    ibnDate: ibnDate,
  };

  onSubmit(payload, generateReport);
};

const onDataMissing = () => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
};

const dataMissingOnSubmit = (description: string) => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "data-missing-review"
  );
  let dataMissingQuestion = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
  if (dataMissingQuestion && dataMissingQuestion.data) {
    dataMissingQuestion.data.description = description;
  }
};

const dataMissingReviewOnSubmit = () => {
  let dataMissingQuestion = questionsState.value.find(
    (item: Question) => item.slug === "data-missing"
  );
  if (dataMissingQuestion && dataMissingQuestion.data) {
    let payload = {
      actionId: 3,
      description: dataMissingQuestion.data.description,
    };
    onSubmit(payload);
  }
};

const onAbnahmeFailed = () => {
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "abnahme-failed"
  );
};

const onAbnahmeFailedSubmit = (payload: {
  description: string;
  whatToSchedule: string;
  days: string;
  missingMaterial: string;
  dispoNotes: string;
  fieldInstruction: string;
}) => {
  let abnahmeFailedQuestion = questionsState.value.find(
    (item: Question) => item.slug === "abnahme-failed"
  );
  if (abnahmeFailedQuestion && abnahmeFailedQuestion.data) {
    abnahmeFailedQuestion.data = payload;
  }
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "abnahme-failed-review"
  );
};

const onAbnahmeFailedReviewSubmit = () => {
  let abnahmeFailedQuestion = questionsState.value.find(
    (item: Question) => item.slug === "abnahme-failed"
  );
  if (abnahmeFailedQuestion && abnahmeFailedQuestion.data) {
    let payload = {
      actionId: 2,
      metadata: abnahmeFailedQuestion.data,
    };
    onSubmit(payload);
  }
};

const onAppointmentNotCompletedSubmit = (payload: {
  description: string;
  whatToSchedule: string;
  days: string;
  missingMaterial: string;
  dispoNotes: string;
  fieldInstruction: string;
}) => {
  let appointmentNotCompletedQuestion = questionsState.value.find(
    (item: Question) => item.slug === "appointment-not-completed"
  );
  if (appointmentNotCompletedQuestion && appointmentNotCompletedQuestion.data) {
    appointmentNotCompletedQuestion.data = payload;
  }
  selectedQuestion.value = questionsState.value.find(
    (item: Question) => item.slug === "appointment-not-completed-review"
  );
};

const onAppointmentNotCompletedReviewSubmit = () => {
  let appointmentNotCompletedQuestions = questionsState.value.find(
    (item: Question) => item.slug === "appointment-not-completed"
  );
  if (
    appointmentNotCompletedQuestions &&
    appointmentNotCompletedQuestions.data
  ) {
    let payload = {
      actionId: 1,
      metadata: appointmentNotCompletedQuestions.data,
    };
    onSubmit(payload);
  }
};

const onStepBack = (goBackToQuestionSlug: string) => {
  let backQuestion = questionsState.value.find(
    (item: Question) => item.slug === goBackToQuestionSlug
  );

  if (backQuestion) {
    selectedQuestion.value = backQuestion;
  }
};

const onSubmit = async (payload: any, generateReport?: boolean) => {
  try {
    isLoading.value = true;
    payload.isQc = false;
    let resp = await client.post(
      `/projects/${props.projectId}/phase/${props.phaseId}/complete-abnahme`,
      payload
    );

    if (resp.status === 200) {
      successToast("QC action saved.");
      if (generateReport) {
        emits("generateReport");
      }
    }
    isLoading.value = false;
    onClose();
    emits("onClose");
  } catch (error: any) {
    console.error(error);
    errorToast(error);
    isLoading.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "nested" }, {
    default: _withCtx(() => [
      (isModalOpen.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(QuestionnaireModal, {
                class: "modal",
                onOnClose: onClose
              }, {
                header: _withCtx(() => [
                  _createElementVNode("div", _hoisted_3, [
                    _cache[8] || (_cache[8] = _createElementVNode("img", {
                      class: "w-4 h-4 cursor-pointer",
                      src: _imports_0,
                      alt: "Pencil"
                    }, null, -1)),
                    _createElementVNode("h2", _hoisted_4, _toDisplayString(selectedQuestion.value ? selectedQuestion.value.modalTitle : ""), 1)
                  ])
                ]),
                "progress-bar": _withCtx(() => [
                  _createVNode(QuestionnaireProgressBar, {
                    progress: questionnaireProgress.value,
                    warning: false
                  }, null, 8, ["progress"])
                ]),
                content: _withCtx(() => [
                  (
                selectedQuestion.value && selectedQuestion.value?.slug === 'abnahme-initial'
              )
                    ? (_openBlock(), _createBlock(AbnahmeInitial, {
                        key: 0,
                        onOnSubmit: abnahmeInitialOnSubmit,
                        "appointment-completed-button-text": 
                _unref(t)('questionnaire.yes_it_is').value
              ,
                        "appointment-not-completed-button-text": 
                _unref(t)('questionnaire.no_there_was_issue').value
              
                      }, null, 8, ["appointment-completed-button-text", "appointment-not-completed-button-text"]))
                    : _createCommentVNode("", true),
                  (
                selectedQuestion.value && selectedQuestion.value?.slug === 'abnahme-outcome'
              )
                    ? (_openBlock(), _createBlock(AbnahmeApointmentCompleted, {
                        key: 1,
                        "appointment-completed-button-text": 
                _unref(t)('questionnaire.abnahme_passed').value
              ,
                        "data-is-missing-button-text": 
                _unref(t)('questionnaire.data_is_missing').value
              ,
                        "apointment-failed-button-text": 
                _unref(t)('questionnaire.abnahme_failed').value
              ,
                        "prev-button-text": _unref(t)('questionnaire.step_back').value,
                        onOnAbnahmePassed: onAbnahmePassed,
                        onOnDataMissing: onDataMissing,
                        onOnAbnahmeFailed: onAbnahmeFailed,
                        onOnStepBack: _cache[0] || (_cache[0] = ($event: any) => (onStepBack('abnahme-initial')))
                      }, null, 8, ["appointment-completed-button-text", "data-is-missing-button-text", "apointment-failed-button-text", "prev-button-text"]))
                    : _createCommentVNode("", true),
                  (
                selectedQuestion.value && selectedQuestion.value?.slug === 'data-missing'
              )
                    ? (_openBlock(), _createBlock(AbnahmeDataMissingTextArea, {
                        key: 2,
                        textAreaTitle: _unref(t)('questionnaire.what_data_is_missing').value,
                        textAreaPlaceholder: _unref(t)('questionnaire.description').value,
                        prevButtonText: _unref(t)('questionnaire.step_back').value,
                        nextButtonText: _unref(t)('questionnaire.next').value,
                        height: "h-[203px]",
                        description: questionsState.value.find( (item) =>
                  item.slug === 'data-missing')?.data.description,
                        onOnSubmit: dataMissingOnSubmit,
                        onOnStepBack: _cache[1] || (_cache[1] = ($event: any) => (onStepBack('abnahme-outcome')))
                      }, null, 8, ["textAreaTitle", "textAreaPlaceholder", "prevButtonText", "nextButtonText", "description"]))
                    : _createCommentVNode("", true),
                  (
                selectedQuestion.value && selectedQuestion.value?.slug === 'abnahme-failed'
              )
                    ? (_openBlock(), _createBlock(AbnahmeAppointmentNotCompleted, {
                        key: 3,
                        title: _unref(t)('questionnaire.abnahme_failed').value,
                        "next-button-text": _unref(t)('questionnaire.next').value,
                        "prev-button-text": _unref(t)('questionnaire.step_back').value,
                        "text-area-placeholder-one": _unref(t)('questionnaire.description').value,
                        "text-area-title-one": 
                _unref(t)('questionnaire.write_what_happened').value
              ,
                        "select-menu-data-one": [
                { id: 'ac', name: 'AC' },
                { id: 'dc', name: 'DC' },
                { id: 'both', name: 'Both' },
              ],
                        "select-menu-data-one-title": 
                _unref(t)('questionnaire.what_do_we_schedule').value
              ,
                        "text-field-one-text": _unref(t)('questionnaire.how_many_days').value,
                        "text-field-one-placeholder": _unref(t)('questionnaire.number').value,
                        "text-area-title-two": 
                _unref(t)('questionnaire.what_material_is_missing').value
              ,
                        "text-area-placeholder-two": _unref(t)('questionnaire.description').value,
                        "text-area-title-three": _unref(t)('questionnaire.dispo_notes').value,
                        "text-area-placeholder-three": 
                _unref(t)('questionnaire.description').value
              ,
                        "text-area-title-four": 
                _unref(t)('questionnaire.what_needs_to_be_done').value
              ,
                        "text-area-placeholder-four": _unref(t)('questionnaire.description').value,
                        "text-field-one-required": true,
                        data: questionsState.value.find(
                (item) => item.slug === 'abnahme-failed' )?.data,
                        onOnStepBack: _cache[2] || (_cache[2] = ($event: any) => (onStepBack('abnahme-outcome'))),
                        onOnSubmit: onAbnahmeFailedSubmit
                      }, null, 8, ["title", "next-button-text", "prev-button-text", "text-area-placeholder-one", "text-area-title-one", "select-menu-data-one-title", "text-field-one-text", "text-field-one-placeholder", "text-area-title-two", "text-area-placeholder-two", "text-area-title-three", "text-area-placeholder-three", "text-area-title-four", "text-area-placeholder-four", "data"]))
                    : _createCommentVNode("", true),
                  (
                selectedQuestion.value &&
                selectedQuestion.value?.slug === 'appointment-not-completed'
              )
                    ? (_openBlock(), _createBlock(AbnahmeAppointmentNotCompleted, {
                        key: 4,
                        data: questionsState.value.find(
                (item) => item.slug === 'appointment-not-completed' )?.data,
                        title: "Appointment not completed",
                        "next-button-text": _unref(t)('questionnaire.next').value,
                        "prev-button-text": _unref(t)('questionnaire.step_back').value,
                        "text-area-placeholder-one": _unref(t)('questionnaire.description').value,
                        "text-area-title-one": 
                _unref(t)('questionnaire.write_what_happened').value
              ,
                        "select-menu-data-one": [
                {
                  id: 'ac',
                  name: 'AC',
                },
                {
                  id: 'dc',
                  name: 'DC',
                },
                {
                  id: 'both',
                  name: 'Both',
                },
              ],
                        "select-menu-data-one-title": 
                _unref(t)('questionnaire.what_do_we_schedule').value
              ,
                        "text-field-one-text": _unref(t)('questionnaire.how_many_days').value,
                        "text-field-one-placeholder": _unref(t)('questionnaire.number').value,
                        "text-area-title-two": 
                _unref(t)('questionnaire.what_material_is_missing').value
              ,
                        "text-area-placeholder-two": _unref(t)('questionnaire.description').value,
                        "text-area-title-three": _unref(t)('questionnaire.dispo_notes').value,
                        "text-area-placeholder-three": 
                _unref(t)('questionnaire.description').value
              ,
                        "text-area-title-four": 
                _unref(t)('questionnaire.what_needs_to_be_done').value
              ,
                        "text-area-placeholder-four": _unref(t)('questionnaire.description').value,
                        "text-field-one-required": true,
                        onOnStepBack: _cache[3] || (_cache[3] = ($event: any) => (onStepBack('abnahme-initial'))),
                        onOnSubmit: onAppointmentNotCompletedSubmit
                      }, null, 8, ["data", "next-button-text", "prev-button-text", "text-area-placeholder-one", "text-area-title-one", "select-menu-data-one-title", "text-field-one-text", "text-field-one-placeholder", "text-area-title-two", "text-area-placeholder-two", "text-area-title-three", "text-area-placeholder-three", "text-area-title-four", "text-area-placeholder-four"]))
                    : _createCommentVNode("", true),
                  (
                selectedQuestion.value &&
                selectedQuestion.value?.slug === 'data-missing-review'
              )
                    ? (_openBlock(), _createBlock(AbnahmeAppointmentFinal, {
                        key: 5,
                        "current-phase": currentPhase.value,
                        "phase-details": __props.phaseDetails,
                        "is-loading": isLoading.value,
                        status: "data-missing",
                        "done-button-text": _unref(t)('questionnaire.submit').value,
                        "final-title": _unref(t)('questionnaire.data_missing').value,
                        "prev-button-text": _unref(t)('questionnaire.back_to_edit').value,
                        answers: dataMissingReviewQuestions.value,
                        onOnStepBack: _cache[4] || (_cache[4] = ($event: any) => (onStepBack('data-missing'))),
                        onOnSubmit: dataMissingReviewOnSubmit
                      }, null, 8, ["current-phase", "phase-details", "is-loading", "done-button-text", "final-title", "prev-button-text", "answers"]))
                    : _createCommentVNode("", true),
                  (
                selectedQuestion.value && selectedQuestion.value?.slug === 'abnahme-passed'
              )
                    ? (_openBlock(), _createBlock(AbnahmeAppointmentFinal, {
                        key: 6,
                        "current-phase": currentPhase.value,
                        "phase-details": __props.phaseDetails,
                        "is-loading": isLoading.value,
                        "text-field-title": `IBN date ${
                currentPhase.value?.phaseId === 2
                  ? '(' + _unref(t)('global.required').value + ')'
                  : '(' + _unref(t)('global.optional').value + ')'
              }`,
                        status: "success",
                        "done-button-text": _unref(t)('questionnaire.done').value,
                        "final-title": _unref(t)('questionnaire.fantastic').value,
                        "final-subtitle": _unref(t)('questionnaire.abnahme_passed').value,
                        "prev-button-text": _unref(t)('questionnaire.back_to_edit').value,
                        onOnStepBack: _cache[5] || (_cache[5] = ($event: any) => (onStepBack('abnahme-outcome'))),
                        onOnSubmit: onAbnahmePassedSubmit
                      }, null, 8, ["current-phase", "phase-details", "is-loading", "text-field-title", "done-button-text", "final-title", "final-subtitle", "prev-button-text"]))
                    : _createCommentVNode("", true),
                  (
                selectedQuestion.value &&
                selectedQuestion.value?.slug === 'abnahme-failed-review'
              )
                    ? (_openBlock(), _createBlock(AbnahmeAppointmentFinal, {
                        key: 7,
                        "current-phase": currentPhase.value,
                        "phase-details": __props.phaseDetails,
                        "is-loading": isLoading.value,
                        status: "failed",
                        "done-button-text": _unref(t)('questionnaire.submit').value,
                        "final-title": _unref(t)('questionnaire.abnahme_failed').value,
                        "prev-button-text": _unref(t)('questionnaire.back_to_edit').value,
                        answers: abnahmeFailedReviewQuestions.value,
                        onOnStepBack: _cache[6] || (_cache[6] = ($event: any) => (onStepBack('abnahme-failed'))),
                        onOnSubmit: onAbnahmeFailedReviewSubmit
                      }, null, 8, ["current-phase", "phase-details", "is-loading", "done-button-text", "final-title", "prev-button-text", "answers"]))
                    : _createCommentVNode("", true),
                  (
                selectedQuestion.value &&
                selectedQuestion.value?.slug === 'appointment-not-completed-review'
              )
                    ? (_openBlock(), _createBlock(AbnahmeAppointmentFinal, {
                        key: 8,
                        "current-phase": currentPhase.value,
                        "phase-details": __props.phaseDetails,
                        status: "failed",
                        "done-button-text": _unref(t)('questionnaire.submit').value,
                        "final-title": _unref(t)('questionnaire.appointment_not_completed').value,
                        "prev-button-text": _unref(t)('questionnaire.back_to_edit').value,
                        "is-loading": isLoading.value,
                        answers: appointmentNotCompletedReviewQuestions.value,
                        onOnStepBack: _cache[7] || (_cache[7] = ($event: any) => (onStepBack('appointment-not-completed'))),
                        onOnSubmit: onAppointmentNotCompletedReviewSubmit
                      }, null, 8, ["current-phase", "phase-details", "done-button-text", "final-title", "prev-button-text", "is-loading", "answers"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})